import React, { Fragment, type RefObject, useRef, useState } from 'react';

import Slider from 'react-slick';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { SliderArrow } from 'Components/SliderArrow';
import Spacer from 'Components/Spacer';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.neutral[400]};
  margin: ${theme.spacing['10x']} 0;
`;

const StyledSlider = styled(Slider)<{ slideNumber: number; isMobile: boolean }>`
  width: 100%;
  margin-top: ${(props) => (props.isMobile ? '-114px' : '0px')};

  .slick-track .slick-slide:last-child {
    padding-right: 12px;
  }
`;

type ReviewProps = {
  stars?: string;
  text?: string;
  name?: string;
  location?: string;
  date?: string;
  isMobile: boolean;
};

const Review = ({ isMobile, stars, text, name, location, date }: ReviewProps) => {
  return (
    <Box
      display="flex"
      flexDirection={{ mobile: 'column', tablet: 'column', desktop: 'row-reverse' }}
      gap="4x"
      paddingBottom={{ mobile: '10x', tablet: '10x', desktop: '10x' }}
      w="full"
    >
      {isMobile && (
        <Box
          display="flex"
          flexDirection="column"
          width={{ mobile: 'full', tablet: 'half', desktop: 'half' }}
        >
          <Text color="primary.300" variant="body4regular">
            {name}
          </Text>
          <Text color="primary.300" variant="body4regular">
            {location}{' '}
          </Text>
          <Text color="primary.300" variant="body4regular">
            {date}
          </Text>
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="column"
        gap="4x"
        width={{ mobile: 'full', tablet: 'full', desktop: 'half' }}
      >
        <Text color="primary.300" variant="body4regular">
          {stars}
        </Text>

        <Text color="primary.300" variant="body4regular">
          {text}
        </Text>
      </Box>
      {!isMobile && (
        <Box
          display="flex"
          flexDirection="column"
          width={{ mobile: 'full', tablet: 'half', desktop: 'half' }}
        >
          <Text color="primary.300" variant="body4regular">
            {name}
          </Text>
          <Text color="primary.300" variant="body4regular">
            {location}{' '}
          </Text>
          <Text color="primary.300" variant="body4regular">
            {date}
          </Text>
        </Box>
      )}
    </Box>
  );
};

const ArrowsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing['2x']};
  /* This below needed to be done to handle the new button placement required for mobile */

  @media (min-width: 767px) {
    margin-top: -130px;
  }
`;

const SliderArrows = ({ sliderRef }: { sliderRef: RefObject<Slider> }) => {
  return (
    <>
      <ArrowsWrapper>
        <SliderArrow
          direction="prev"
          onClick={() => sliderRef?.current?.slickPrev()}
          positioning="initial"
        />
        <SliderArrow
          direction="next"
          onClick={() => sliderRef?.current?.slickNext()}
          positioning="initial"
        />
      </ArrowsWrapper>
      <Spacer size={64} />
    </>
  );
};

/**
 * Utility function to chunk array into grouped arrays
 * @param array the flat array with all the reviews
 * @param size the size of the grouped reviews (grouped by 3 here)
 * @returns an array containing x arrays of the grouped reviews
 */

function chunkArray<T>(array: T[], size: number): T[][] {
  // Math.ceil(array.length / size) defines the length of the array depending of the spliting rule
  return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
    // building each array splitting data
    array.slice(index * size, index * size + size),
  );
}

type ReviewType = {
  name: string;
  date: string;
  location: string;
  stars: string;
  text: string;
};

type ProductReviewsProps = {
  reviews: { name: string; date: string; location: string; stars: string; text: string }[];
};

const ReviewSlider = ({ reviews }: ProductReviewsProps) => {
  const [_currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);
  const { isMobile } = useResponsiveVariant();

  const groupedReviews: ReviewType[][] = isMobile
    ? chunkArray<ReviewType>(reviews, 1)
    : chunkArray<ReviewType>(reviews, 3);

  const reviewsCards = groupedReviews.map((group, index) => (
    <Fragment key={index}>
      {group.map((review, i) => (
        <Fragment key={`${review.name}-${i}`}>
          <Review
            key={`${review.name}-${i}`}
            date={review.date}
            isMobile={isMobile}
            location={review.location}
            name={review.name}
            stars={review.stars}
            text={review.text}
          />
          {i < group.length - 1 && <Separator />}
        </Fragment>
      ))}
    </Fragment>
  ));

  return (
    <>
      <SliderArrows sliderRef={sliderRef} />

      <StyledSlider
        ref={sliderRef}
        afterChange={(slide: number) => setCurrentSlide(Math.round(slide))}
        arrows={false}
        data-testId="review-slider"
        focusOnSelect={false}
        infinite
        isMobile={isMobile}
        slideNumber={reviewsCards.length - 1}
        slidesToShow={1}
      >
        {reviewsCards}
      </StyledSlider>
    </>
  );
};

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  p {
    max-width: 400px;
  }
`;

export const ProductReviews = React.forwardRef<HTMLDivElement, ProductReviewsProps>(
  ({ reviews }, ref) => {
    const { isMobile } = useResponsiveVariant();

    return (
      <Box
        ref={ref}
        paddingX={{ mobile: '8x', tablet: '15x' }}
        paddingY={{ mobile: '12x', tablet: '20x', desktop: '30x' }}
        w="full"
      >
        <HeaderWrapper>
          <Text as="h2" color="primary.400" textAlign="left" variant="h3">
            Tried, tested, truly loved
          </Text>
          <Spacer size={12} />
          <Text color="primary.300" variant="body4regular">
            See what people are saying about Custom Scalp Serum. {!isMobile && <br />} Our validated
            reviews feature real results from real users.
          </Text>
        </HeaderWrapper>
        <Box width="full">
          <Separator />
        </Box>
        <ReviewSlider reviews={reviews} />
      </Box>
    );
  },
);

ProductReviews.displayName = 'ProductReviews';
